import axios, { type AxiosInstance, type AxiosRequestConfig } from 'axios'
import { message } from 'antd'
import { useAuthStore } from '@/stores/auth'
import type { ApiResponse, PageResponse } from '@/types'

/** Axios 实例 */
const request: AxiosInstance = axios.create({
  baseURL: '/api/v1',
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
})

/** 请求拦截器：自动注入 token */
request.interceptors.request.use(
  (config) => {
    const token = useAuthStore.getState().token
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  (error) => Promise.reject(error),
)

/** 响应拦截器：统一处理业务错误 */
request.interceptors.response.use(
  (response) => {
    const data = response.data as ApiResponse
    // 业务错误
    if (data.code !== 200) {
      message.error(data.message || '请求失败')
      return Promise.reject(new Error(data.message))
    }
    return response.data
  },
  (error) => {
    // HTTP 错误
    if (error.response) {
      const { status, data } = error.response
      if (status === 401) {
        // 登录接口的 401 是账号/密码错误，不属于会话过期：直接展示后端原因，不做登出跳转
        const isLoginRequest = error.config?.url?.includes('/auth/login')
        if (isLoginRequest) {
          message.error(data?.message || '用户名或密码错误')
        } else {
          message.error('登录已过期，请重新登录')
          useAuthStore.getState().logout()
          window.location.href = '/login'
        }
      } else if (status === 403) {
        message.error('权限不足')
      } else if (status === 404) {
        message.error('资源不存在')
      } else {
        // FastAPI 422 的 detail 是对象数组（[{type, loc, msg}]），直接渲染会崩溃，需转为可读文本
        const detail = data?.detail
        const detailText =
          typeof detail === 'string'
            ? detail
            : Array.isArray(detail)
              ? detail.map((d: any) => d?.msg).filter(Boolean).join('; ')
              : ''
        message.error(data?.message || detailText || `请求错误 (${status})`)
      }
    } else if (error.request) {
      message.error('网络异常，请检查网络连接')
    } else {
      message.error(error.message || '未知错误')
    }
    return Promise.reject(error)
  },
)

/**
 * 统一类型断言：响应拦截器已将 AxiosResponse 展开为响应体（ApiResponse），
 * 此处对齐运行时实际类型（TS 无法推断拦截器返回值，需显式断言）
 */
async function unwrap<T>(p: Promise<unknown>): Promise<ApiResponse<T>> {
  return (await p) as ApiResponse<T>
}

/** 封装 GET */
export async function get<T = any>(url: string, params?: any, config?: AxiosRequestConfig): Promise<T> {
  const res = await unwrap<T>(request.get(url, { params, ...config }))
  return res.data
}

/** 封装 POST */
export async function post<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
  const res = await unwrap<T>(request.post(url, data, config))
  return res.data
}

/** 封装 POST（返回完整响应体，供调用方取后端 message 提示） */
export async function postFull<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
  return unwrap<T>(request.post(url, data, config))
}

/** 封装 PUT */
export async function put<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
  const res = await unwrap<T>(request.put(url, data, config))
  return res.data
}

/** 封装 DELETE */
export async function del<T = any>(url: string, config?: AxiosRequestConfig): Promise<T> {
  const res = await unwrap<T>(request.delete(url, config))
  return res.data
}

/** 封装分页 GET */
export async function getPage<T = any>(url: string, params?: any): Promise<{ data: T[]; total: number; page: number; page_size: number }> {
  const res = (await request.get(url, { params })) as PageResponse<T>
  return {
    data: res.data,
    total: res.total,
    page: res.page,
    page_size: res.page_size,
  }
}

export default request
